import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Download, Lock, CheckCircle2, FileText, Loader2, Mail, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { trackPurchase, identifyUser } from "../lib/tiktok";

// Google Ads — IPO Access Dossier Purchase conversion. Fires only once per
// session and uses the Stripe session id as transaction_id so Google
// de-duplicates if the buyer refreshes the success page.
const fireGoogleAdsPurchase = (transactionId) => {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "conversion", {
      send_to: "AW-18239642780/eSJQCMzv7r4cEJy5q_lD",
      value: 1.0,
      currency: "EGP",
      transaction_id: transactionId || "",
    });
  } catch (_) { /* analytics must never break the flow */ }
};

const FONT = "'Inter','SF Pro Display',system-ui,-apple-system,sans-serif";
const SERIF = "'Fraunces','Playfair Display',Georgia,serif";

const ensureFonts = () => {
  if (document.getElementById("ipo-font")) return;
  const l = document.createElement("link");
  l.id = "ipo-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Fraunces:wght@400;500;600;700&display=swap";
  document.head.appendChild(l);
};

export default function IPODownload() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const csId = params.get("cs") || "";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileErr, setReconcileErr] = useState("");
  const [err, setErr] = useState("");
  // Prevents double-firing TikTok Purchase + Google Ads conversion if the
  // order is observed paid through more than one code path (auto poll,
  // auto reconcile, manual reconcile).
  const purchaseFiredRef = useRef(false);

  const firePurchasePixels = (o) => {
    if (purchaseFiredRef.current) return;
    purchaseFiredRef.current = true;
    try { if (o?.email) identifyUser({ email: o.email, externalId: token }); } catch (_) { /* analytics never breaks the flow */ }
    try { if (csId) trackPurchase(csId, "ipo_access_dossier"); } catch (_) { /* analytics never breaks the flow */ }
    fireGoogleAdsPurchase(csId || token);
  };

  // Manual reconcile — used by the "Verify with Stripe now" button when the
  // buyer doesn't want to wait for the automatic fallback poll.
  const manualReconcile = async () => {
    setReconcileErr("");
    setReconciling(true);
    try {
      const { data } = await api.post(`/ipo/reconcile/${token}`);
      if (data?.status === "paid") {
        // refetch order to get the fully-populated paid state, then run the
        // same post-payment side-effects as the auto poll.
        try {
          const { data: o } = await api.get(`/ipo/order/${token}`);
          setOrder(o);
          firePurchasePixels(o);
        } catch (_) { /* swallow */ }
      } else if (data?.reason) {
        setReconcileErr(data.reason);
      } else {
        setReconcileErr("Stripe says payment is still pending. Try again in 10–20 seconds.");
      }
    } catch (e) {
      setReconcileErr(e?.response?.data?.detail || "Could not reach Stripe — try again.");
    } finally {
      setReconciling(false);
    }
  };
  // We poll briefly so a slightly-delayed Stripe webhook doesn't strand the
  // buyer on a "not paid yet" screen. After a short grace period we fall back
  // to a direct Stripe API reconcile so the buyer is NEVER stuck if Stripe
  // confirms the payment but the webhook is dropped/delayed.
  useEffect(() => {
    ensureFonts();
    document.title = "Your IPO Access Dossier™ — Download";
    let cancelled = false;
    let attempts = 0;
    let reconciled = false;

    const tryReconcile = async () => {
      if (reconciled) return false;
      reconciled = true;
      try {
        const { data } = await api.post(`/ipo/reconcile/${token}`);
        if (data?.status === "paid") return true;
      } catch (_) { /* swallow — outer poll will keep trying */ }
      return false;
    };

    const poll = async () => {
      while (!cancelled && attempts < 24) {
        attempts += 1;
        try {
          const { data } = await api.get(`/ipo/order/${token}`);
          if (cancelled) return;
          setOrder(data);
          if (data.status === "paid") {
            setLoading(false);
            firePurchasePixels(data);
            return;
          }
        } catch (e) {
          if (cancelled) return;
          if (e?.response?.status === 404) {
            setErr("This download link is invalid or has expired.");
            setLoading(false);
            return;
          }
        }
        // After ~6 seconds of waiting on the webhook, ask Stripe directly.
        if (attempts === 4) {
          const flipped = await tryReconcile();
          if (flipped && !cancelled) continue; // next loop iteration will see paid
        }
        await new Promise((r) => setTimeout(r, attempts < 4 ? 1500 : 3000));
      }
      // Final reconcile attempt before giving up — covers the rare case where
      // /ipo/order kept returning pending the whole time but Stripe is paid.
      if (!cancelled && !reconciled) {
        await tryReconcile();
        try {
          const { data } = await api.get(`/ipo/order/${token}`);
          if (!cancelled) setOrder(data);
          if (!cancelled && data?.status === "paid") {
            firePurchasePixels(data);
          }
        } catch (_) { /* swallow */ }
      }
      if (!cancelled) setLoading(false);
    };
    poll();
    return () => { cancelled = true; };
  }, [token, csId]);

  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/ipo/download/${token}`;

  return (
    <div
      dir="ltr"
      lang="en"
      className="min-h-screen bg-[#0A0D12] text-[#E7EAF0] selection:bg-[#3F8EFC]/40 flex flex-col"
      style={{ fontFamily: FONT, textAlign: "left" }}
      data-testid="ipo-download-root"
    >
      {/* NAV */}
      <nav className="px-5 pt-5 max-w-5xl w-full mx-auto flex items-center justify-between">
        <Link to="/ipoaccess" className="flex items-center gap-2.5" data-testid="ipo-download-logo">
          <img src="/ipo-access-dossier-logo.jpg" alt="IPO Access Dossier" className="h-9 w-9 rounded-sm object-cover" />
          <div className="leading-tight">
            <div className="font-bold text-[13px] tracking-tight">IPO Access Dossier<span className="text-[#3F8EFC]">™</span></div>
            <div className="text-[9.5px] text-[#7A8597] tracking-[0.18em]">EARLY ACCESS · INSIGHT · ADVANTAGE</div>
          </div>
        </Link>
        <span className="text-[11px] text-[#7A8597] flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-[#3F8EFC]" /> Private download
        </span>
      </nav>

      <main className="flex-1 px-5 py-12 sm:py-20 flex items-center justify-center">
        <div className="max-w-xl w-full">
          {loading && (
            <div className="text-center py-12" data-testid="ipo-download-loading">
              <Loader2 className="w-7 h-7 text-[#3F8EFC] animate-spin mx-auto mb-4" />
              <div className="text-[15px] text-white font-semibold">Confirming your payment…</div>
              <div className="text-[12.5px] text-[#7A8597] mt-1.5">This usually takes a couple of seconds.</div>
            </div>
          )}

          {!loading && order?.status === "paid" && (
            <div data-testid="ipo-download-success">
              <div className="text-center mb-9">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#3F8EFC]/[0.15] border border-[#3F8EFC]/40 mb-5">
                  <CheckCircle2 className="w-7 h-7 text-[#3F8EFC]" strokeWidth={1.8} />
                </div>
                <div className="text-[10.5px] tracking-[0.28em] text-[#3F8EFC] font-bold mb-3">PAYMENT CONFIRMED</div>
                <h1 className="font-bold text-[30px] sm:text-[40px] leading-[1.1] tracking-tight text-white" style={{ fontFamily: SERIF, fontWeight: 600 }}>
                  Your Dossier Is Ready.
                </h1>
                <p className="mt-4 text-[14.5px] text-[#A5AFC0] leading-[1.75]">
                  Thank you for your purchase. A copy is also linked to <span className="text-white font-semibold">{order.email}</span>.
                </p>
              </div>

              {/* Download card */}
              <div className="bg-[#0E1218] border border-[#3F8EFC]/40 rounded-sm p-6 shadow-[0_30px_80px_-25px_rgba(63,142,252,0.55)]" data-testid="ipo-download-card">
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 rounded-sm bg-gradient-to-br from-[#3F8EFC] to-[#1E5BD8] flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-white" strokeWidth={2} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.24em] text-[#7A8597] font-bold">DOSSIER № 001</div>
                    <div className="text-[15px] font-bold text-white truncate">IPO Access Dossier — June 2026</div>
                    <div className="text-[11.5px] text-[#7A8597] mt-0.5">PDF · ${order.amount_usd?.toFixed(2)} USD · One-time purchase</div>
                  </div>
                </div>

                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="ipo-download-btn"
                  className="mt-6 w-full bg-[#3F8EFC] hover:bg-[#5BA0FF] text-white font-bold text-[15px] tracking-wide px-7 py-4 rounded-sm transition-all flex items-center justify-center gap-2 min-h-[54px] shadow-[0_10px_36px_-10px_rgba(63,142,252,0.65)]"
                >
                  <Download className="w-4 h-4" /> Download The Dossier (PDF)
                </a>
                <div className="mt-3 text-[11.5px] text-[#7A8597] flex items-center justify-center gap-1.5">
                  <Lock className="w-3 h-3 text-[#3F8EFC]" /> This link is private to you. Save the file once downloaded.
                </div>
              </div>

              <div className="mt-8 text-[12.5px] text-[#7A8597] leading-relaxed text-center">
                Question or didn&apos;t receive your email copy? Reach out and we&apos;ll resend.
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-[#A5AFC0]">
                  <Mail className="w-3.5 h-3.5" /> support@replyrescue.app
                </div>
              </div>
            </div>
          )}

          {!loading && order && order.status !== "paid" && (
            <div className="text-center py-10" data-testid="ipo-download-pending">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FBBF24]/[0.12] border border-[#FBBF24]/40 mb-5">
                <Loader2 className="w-6 h-6 text-[#FDE68A] animate-spin" />
              </div>
              <h1 className="text-[22px] font-bold text-white">Almost there — confirming your payment</h1>
              <p className="mt-3 text-[13.5px] text-[#A5AFC0] leading-relaxed max-w-md mx-auto">
                Stripe is still finalising on its end. We&apos;ll check in with Stripe directly to confirm — this usually takes a few seconds.
              </p>
              <button
                onClick={manualReconcile}
                disabled={reconciling}
                data-testid="ipo-download-reconcile"
                className="mt-6 inline-flex items-center gap-2 bg-[#3F8EFC] hover:bg-[#5BA0FF] text-white font-bold text-[13.5px] tracking-wide px-6 py-3 rounded-sm transition-all min-h-[46px] disabled:opacity-60"
              >
                {reconciling ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying with Stripe…</> : "Verify with Stripe now"}
              </button>
              {reconcileErr && (
                <div className="mt-4 text-[12.5px] text-[#FCA5A5] max-w-md mx-auto" data-testid="ipo-reconcile-err">
                  {reconcileErr}
                </div>
              )}
              <div className="mt-3 text-[11.5px] text-[#7A8597]">
                Charged on your card but still stuck? Email <a href="mailto:support@replyrescue.app" className="text-[#3F8EFC] hover:underline">support@replyrescue.app</a> with your receipt.
              </div>
            </div>
          )}

          {!loading && err && !order && (
            <div className="text-center py-10" data-testid="ipo-download-error">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F87171]/[0.12] border border-[#F87171]/40 mb-5">
                <AlertTriangle className="w-6 h-6 text-[#FCA5A5]" />
              </div>
              <h1 className="text-[22px] font-bold text-white">Link not found</h1>
              <p className="mt-3 text-[13.5px] text-[#A5AFC0] leading-relaxed max-w-md mx-auto">{err}</p>
              <Link
                to="/ipoaccess"
                className="mt-6 inline-flex items-center gap-2 bg-[#3F8EFC] hover:bg-[#5BA0FF] text-white font-bold text-[13.5px] tracking-wide px-6 py-3 rounded-sm transition-all min-h-[46px]"
              >
                Back to IPO Access
              </Link>
            </div>
          )}
        </div>
      </main>

      <footer className="px-5 py-7 border-t border-white/[0.06] text-center">
        <p className="text-[11px] text-[#5A6275] max-w-lg mx-auto leading-relaxed">
          IPO Access Dossier™ is an independent research product. Nothing in this product constitutes investment, legal, or tax advice.
        </p>
      </footer>
    </div>
  );
}
